import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Controls, SmsResponse } from '../../model/model';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { FilterpopupComponent } from './filterpopup/filterpopup.component';
import { MatDialog } from '@angular/material/dialog';
import { DataService } from '../../services/data.service';
import { ValueService } from '../../shared/value.service';
import { FilterService } from '../../shared/filter.service';
import { one, pageSize, zero, ApplicationStatus, StatusReason } from '../../shared/constant';

@Component({
  selector: 'app-result',
  templateUrl: './result.component.html',
  styleUrl: './result.component.css',
  standalone: false
})
export class ResultComponent {
  applicationID = '';
  actualData: SmsResponse[] = [];
  data!: Controls;
  errorMessage = '';
  isDataEmpty = false;
  currentPage: number = one;
  totalPage: number = zero;
  inputValue = '';
  sortDirection: { [key: string]: string } = {};
  isLoading = false;
  
  dataSource = new MatTableDataSource<SmsResponse>();
  displayedColumns: string[] = [
    'memberFirstName',
    'memberLastName',
    'applicationStatus',
    'statusReason',
    'statusReasonDescription',
    'applicationSystemSubmitCode',
    'applicationID',
    'coverageEffectiveDate',
    'submisssionDate',
    'lastUpdatedDate',
    'planName',
    'planNumber',
    'productType'
  ];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private readonly dialog: MatDialog,
    private readonly dataservice: DataService,
    private readonly sharedService: ValueService,
    private readonly filterService: FilterService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.dataSource.data = this.actualData.slice();
  }

  ngOnInit(): void {
    this.sharedService.currentMessage.subscribe((message) => {
      this.data = message;

      this.sharedService.clearAllExceptInput(message);
    });

     this.getTableData(); 

    this.displayedColumns.forEach(
      (column) => (this.sortDirection[column] = '')
    );
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.paginator.page.subscribe(() => {
      this.currentPage = this.paginator.pageIndex + one;
      this.cdr.detectChanges();
    });
  }

  getTableData() {
    this.errorMessage = '';
    this.isLoading = true;
    this.dataservice.callApi(this.data).then(
      (result) => {
        this.isLoading = false;
        this.actualData = result.smsResponse;
        this.dataSource.data = result.smsResponse;
        this.isDataEmpty = !result.smsResponse || result.smsResponse.length <= zero;
        this.calculateTotalPages();
        this.filterService.clearAllFilter();
      },
      (error) => {
        this.isLoading = false;
        this.errorMessage = 'An error occured while fetching data.';
      }
    );
  }

  search(event: Event): void {
    const regex = /^[a-zA-Z0-9]*$/;
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;
    if (!regex.test(inputValue)) {
      target.value = inputValue.replace(/[^a-zA-Z0-9]/g, '');
    }
    this.inputValue = target.value;
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
    this.dataSource.filterPredicate = (data: SmsResponse, filter: string) => {
      const searchValue = filter.trim().toLowerCase();
      return data.applicationID.toLowerCase().includes(searchValue);
    };
    this.dataSource.filter = filterValue;
    this.currentPage = one;
    this.calculateTotalPages();
    this.filterService.clearAllFilter();
  }

  openFilterDialog(column: string): void {
    const columnData = this.getColumnData(column);
    const dialogRef = this.dialog.open(FilterpopupComponent, {
      width: '250px',
      data: { columnData },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.length > zero) {
        this.applyFilter(result, column);
      } else {
        this.clearFilter(column);
      }
    });
  }

  getColumnData(column: string) {
    return [
      ...new Set(this.dataSource.data.map((item) => item[column])),
    ].filter((value) => value !== null && value !== undefined && value !== '');
  }

  applyFilter(filterValues: string, column: string) {
    this.dataSource.filterPredicate = (data, filter) => {
      if (filter.length === zero) {
        return true;
      }
      const columnValue = data[column]?.toString() ?? '';
      return filter.includes(columnValue);
    };
    this.dataSource.filter = filterValues;
    this.paginator.pageIndex = zero;
    this.currentPage = one;
    this.calculateTotalPages();
    this.cdr.detectChanges();
  }

  clearFilter(column: string) {
    this.filterService.clearFilter(column);
  }

  nextPage(): void {
    if (this.paginator.hasNextPage()) {
      this.paginator.nextPage();
      this.currentPage = this.paginator.pageIndex + one;
      this.cdr.detectChanges();
    }
  }

  prevPage(): void {
    if (this.paginator.hasPreviousPage()) {
      this.paginator.previousPage();
      this.currentPage = this.paginator.pageIndex + one;
      this.cdr.detectChanges();
    }
  }

  calculateTotalPages() {
    const currentPageSize = this.paginator.pageSize || pageSize;
    const totalItems =
      this.dataSource.filteredData.length || this.paginator.length;
    this.totalPage = Math.ceil(totalItems / currentPageSize);
  }

  sortData(column: string): void {
    if (!this.sortDirection[column] || this.sortDirection[column] === '') {
      this.sortDirection[column] = 'asc';
    }
    const currentDirection = this.sortDirection[column];

    const sortedData = [...this.dataSource.data].reverse();

    sortedData.sort((a: SmsResponse, b: SmsResponse) => {
      let comparison = 0;
      if (
        [
          'applicationID',
          'memberFirstName',
          'memberLastName',
          'planNumber',
          'productType',
          'applicationStatus',
          'applicationSystemSubmitCode',
        ].includes(column)
      ) {
        comparison = this.compareGroup1(a, b, column);
      } else {
        comparison = this.compareGroup2(a, b, column);
      }
      return comparison;
    });

    if (currentDirection === 'asc') {
      sortedData.reverse();
    }

    this.dataSource.data = sortedData;
    this.sortDirection[column] = currentDirection === 'asc' ? 'desc' : 'asc';
  }

  compareGroup1(a: SmsResponse, b: SmsResponse, column: string): number {
    switch (column) {
      case 'applicationID':
        return this.compareWithNullDate(a, b, 'applicationID');
      case 'memberFirstName':
        return this.compareWithNullDate(a, b, 'memberFirstName');
      case 'memberLastName':
        return this.compareWithNullDate(a, b, 'memberLastName');
      case 'planNumber':
        return this.compareWithNullDate(a, b, 'planNumber');
      case 'productType':
        return this.compareWithNullDate(a, b, 'productType');
      case 'applicationStatus':
        return this.compareWithNullDate(a, b, 'applicationStatus');
      case 'applicationSystemSubmitCode':
        return this.compareWithNullDate(a, b, 'applicationSystemSubmitCode');
      default:
        return 0;
    }
  }

  compareGroup2(a: SmsResponse, b: SmsResponse, column: string): number {
    switch (column) {
      case 'statusReason':
        return this.compareWithNullDate(a, b, 'statusReason');
      case 'statusReasonDescription':
        return this.compareWithNullDate(a, b, 'statusReasonDescription');
      case 'planName':
        return this.compareWithNullDate(a, b, 'planName');
      case 'coverageEffectiveDate':
        return this.compareWithNullDate(a, b, 'coverageEffectiveDate', true);
      case 'submisssionDate':
        return this.compareWithNullDate(a, b, 'submisssionDate', true);
      case 'lastUpdatedDate':
        return this.compareWithNullDate(a, b, 'lastUpdatedDate', true);
      default:
        return 0;
    }
  }

  compareWithNullDate<T>(
    a: T,
    b: T,
    key: keyof T,
    isDate = false
  ): number {
    const aValue = a[key];
    const bValue = b[key];

    if (aValue === null && bValue !== null) {
      return -one;
    } else if (aValue !== null && bValue === null) {
      return one;
    } else if (aValue === null && bValue === null) {
      return zero;
    } else if (isDate) {
      return (
        new Date(aValue as string).getTime() -
        new Date(bValue as string).getTime()
      );
    } else {
      return this.compareValues(aValue, bValue);
    }
  }

  compareValues<T>(aValue: T, bValue: T): number {
    if (aValue < bValue) {
      return -one;
    } else if (aValue > bValue) {
      return one;
    } else {
      return zero;
    }
  }

    isRedStatus(applicationStatus: string, statusReason: string): boolean {
        return (
            applicationStatus === ApplicationStatus.CancelledApplication ||
            applicationStatus === ApplicationStatus.InactivePolicy ||
            !applicationStatus
        );
    }

    isGreenStatus(applicationStatus: string, statusReason: string): boolean {
        return (
            applicationStatus === ApplicationStatus.ActivePolicy ||
            (applicationStatus === ApplicationStatus.FutureActivePolicy &&
                (statusReason === StatusReason.AccretedWithCMS ||
                    statusReason === StatusReason.PlanTransition ||
                    statusReason === StatusReason.PolicyIssued)
            )
        );
    }

    isBlackStatus(applicationStatus: string, statusReason: string): boolean {
        return (
            applicationStatus === ApplicationStatus.PendedApplication ||
            applicationStatus === ApplicationStatus.SubmittedApplication ||
            applicationStatus === ApplicationStatus.InProgressApplication ||
            (applicationStatus === ApplicationStatus.FutureActivePolicy &&
                statusReason === StatusReason.NotAccretedWithCMS)
        );
    }

    getRowClass(applicationStatus: string, statusReason: string): string {
        const statusClasses: { [key: string]: boolean } = {
            'red-row': this.isRedStatus(applicationStatus, statusReason),
            'green-row': this.isGreenStatus(applicationStatus, statusReason),
            'black-row': this.isBlackStatus(applicationStatus, statusReason)
        };

        return Object.keys(statusClasses).filter(key => statusClasses[key]).join(' ');
    }

}
